import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { AppConfig } from "../AppConfig";
import { LocalStorageManager } from "../core/storage/LocalStorageManager";
import { appActions } from "../ducks/app";
import { Campaign } from "../ducks/campaign";
import { Category, Menu, menuActions } from "../ducks/menu";
import { Store, storeActions } from "../ducks/store";
import { getCurrentDateUTC } from "../utils/date";
import { useAuth } from "./useAuth";
import { CouponHook } from "./useCoupon";
import { useNavigation } from "./useNavigation";
import { useStore } from "./useStore";

export const useMenu = (
  useCoupon: CouponHook,
  storeCode?: string,
  date?: string,
) => {
  const dispatch = useDispatch();
  const { navigateToTopPage } = useNavigation();
  const { isLoggedIn } = useAuth();
  const { setFavoriteStore } = useStore();

  const [store, setStore] = useState<Store | null>(null);

  const [categories, setCategories] = useState<Category[]>([]);
  const [favoriteMenus, setFavoriteMenus] = useState<Menu[]>([]);
  const [recommendedMenus, setRecommendedMenus] = useState<Menu[]>([]);
  const [limitedMenus, setLimitedMenus] = useState<Menu[]>([]);

  const [menuMapById, setMenuMapById] = useState<Map<number, Menu> | null>(
    null,
  );

  const { appliedCoupons, selectedAppCoupons } = useCoupon;

  const campaign = LocalStorageManager.Instance.getObject<Campaign>(
    AppConfig.Instance.LocalStorageKey["campaign"],
  );

  useEffect(() => {
    if (!storeCode || !date) return;

    dispatch(appActions.showLoading());
    dispatch(
      storeActions.getStores.started({
        conditions: [
          {
            name: "店舗コード",
            operator: "equal",
            value: storeCode,
          },
        ],
        onError: () => dispatch(appActions.hideLoading()),
        onSuccess: (stores) => {
          if (stores.length === 0) {
            dispatch(
              appActions.showModal.started({
                title: "該当する店舗がありません",
                subTitle: "検索内容を変更してもう一度お試しください。",
                nextButtonLabel: "閉じる",
                handleNext: () => {
                  dispatch(appActions.closeModal());
                  navigateToTopPage();
                },
              }),
            );
            return;
          }

          setStore(stores[0]);
          // searchMenus(stores[0].id, date);
        },
      }),
    );
  }, []);

  const searchMenus = useCallback(
    (storeId: number, pickupDate: string) => {
      dispatch(
        menuActions.getMenus.started({
          store_id: storeId,
          pickup_date: pickupDate,
          nowTime: getCurrentDateUTC(),
          early_bird: !!campaign ? 1 : undefined,
          coupons: [
            ...(selectedAppCoupons
              ? selectedAppCoupons.map((coupon) => coupon.code)
              : []),
            ...(selectedAppCoupons && appliedCoupons
              ? appliedCoupons.filter(
                  (coupon) =>
                    !selectedAppCoupons.some((c) => c.code === coupon.code),
                )
              : (appliedCoupons ?? [])
            ).map((coupon) => coupon.code),
          ],
          onComplete: () => dispatch(appActions.hideLoading()),
          onSuccess: (categories) => {
            setCategories(categories);

            const favorites: Menu[] = [];
            const recommended: Menu[] = [];
            const limited: Menu[] = [];
            const menuMap = new Map<number, Menu>();
            categories.forEach((category) => {
              category.menus.forEach((menu) => {
                if (menu.isFavorite) favorites.push(menu);
                if (menu.isRecommended) recommended.push(menu);
                if (menu.isLimitedMenu) limited.push(menu);

                menuMap.set(menu.id, menu);
              });
            });
            setFavoriteMenus(favorites);
            setRecommendedMenus(
              recommended.sort((a, b) => a.recommendOrder! - b.recommendOrder!),
            );
            setLimitedMenus(limited);
            setMenuMapById(menuMap);
          },
        }),
      );
    },
    [selectedAppCoupons, appliedCoupons, campaign],
  );

  const categoriesToDisplay = useMemo(() => {
    if (favoriteMenus.length === 0) return categories;

    return [
      {
        id: -1,
        name: "お気に入り",
        sortOrder: -1,
        count: favoriteMenus.length,
        menus: favoriteMenus,
        text: "",
      },
      ...categories,
    ];
  }, [categories, favoriteMenus]);

  const setMultipleFavoriteMenus = useCallback(
    (menuCodes: string[], isFavorite: boolean, onSuccess?: () => void) => {
      if (!isLoggedIn) {
        return dispatch(
          appActions.showModal.started({
            title: "ログインしてください",
            nextButtonLabel: "閉じる",
            handleNext: () => dispatch(appActions.closeModal()),
          }),
        );
      }

      dispatch(
        menuActions.setFavoriteMenu.started({
          likes: menuCodes.map((menuCode) => ({
            menu_code: menuCode,
            enable: isFavorite,
          })),
          onSuccess: () => {
            const newFavoriteMenus: Menu[] = [];
            const newRecommendedMenus: Menu[] = [];
            const newLimitedMenus: Menu[] = [];
            const newCategories = categories.map((category) => {
              const newMenus = category.menus.map((menu) => {
                if (menu.isFavorite) newFavoriteMenus.push(menu);
                if (menu.isRecommended) newRecommendedMenus.push(menu);
                if (menu.isLimitedMenu) newLimitedMenus.push(menu);

                if (menuCodes.includes(menu.code)) {
                  if (isFavorite)
                    newFavoriteMenus.push({ ...menu, isFavorite });
                  else
                    newFavoriteMenus.splice(newFavoriteMenus.indexOf(menu), 1);

                  if (menu.isRecommended) {
                    newRecommendedMenus.pop();
                    newRecommendedMenus.push({ ...menu, isFavorite });
                  }

                  if (menu.isLimitedMenu) {
                    newLimitedMenus.pop();
                    newLimitedMenus.push({ ...menu, isFavorite });
                  }

                  return { ...menu, isFavorite };
                }
                return menu;
              });

              return { ...category, menus: newMenus };
            });

            setCategories(newCategories);
            setFavoriteMenus(newFavoriteMenus);
            setRecommendedMenus(
              newRecommendedMenus.sort(
                (a, b) => a.recommendOrder! - b.recommendOrder!,
              ),
            );
            setLimitedMenus(newLimitedMenus);
            onSuccess?.();
          },
        }),
      );
    },
    [categories, isLoggedIn],
  );

  const handleSetFavoriteStore = useCallback(
    (favorite: boolean, onSuccess?: () => void) => {
      if (!store) return;

      setFavoriteStore(store.id, favorite, () => {
        setStore({ ...store, isFavorite: favorite });
        onSuccess?.();
      });
    },
    [store],
  );

  const handleSetFavoriteMenu = useCallback(
    (
      menuId: number,
      menuCode: string,
      favorite: boolean,
      onSuccess?: () => void,
    ) => {
      if (!menuMapById) return;

      setMultipleFavoriteMenus([menuCode], favorite, () => {
        const menu = menuMapById.get(menuId);
        if (menu) {
          setMenuMapById(
            new Map(menuMapById.set(menuId, { ...menu, isFavorite: favorite })),
          );
        }

        onSuccess?.();
      });
    },
    [setMultipleFavoriteMenus, menuMapById],
  );

  return {
    store,
    menuMapById,
    recommendedMenus,
    limitedMenus,
    searchMenus,
    setCategories,
    categoriesToDisplay,
    setMultipleFavoriteMenus,
    favoriteMenus,
    handleSetFavoriteStore,
    handleSetFavoriteMenu,
  };
};
